import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthGate } from '@nsi-advisory-services/auth-react';
import { AuthProvider } from './contexts/AuthContext';

/* Pages */
import EmployeesPage from './pages/EmployeesPage';
import MePage from './pages/MePage';
import TimeOffPage from './pages/TimeOffPage';
import TimeOffCreatePage from './pages/TimeOffCreatePage';
import TimeOffManagePage from './pages/TimeOffManagePage';
import TimeOffCalendarViewPage from './pages/TimeOffCalendarViewPage';
import TimeOffPlannerPage from './pages/TimeOffPlannerPage';
import VacationPlansPage from './pages/VacationPlansPage';
import TimeOffRequestPage from './pages/TimeOffRequestPage';
import ApprovalsPage from './pages/ApprovalsPage';
import ApprovalsGridPage from './pages/ApprovalsGridPage';
import ApprovalsInboxPage from './pages/ApprovalsInboxPage';
import ExpensePayrollViewPage from './pages/ExpensePayrollViewPage';
import ExpensePage from './pages/ExpensePage';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import CloseDatesPage from './pages/CloseDatesPage';
import HiringDashboardPage from './pages/HiringDashboardPage';
import ViewHoursPage from './pages/ViewHoursPage';
import CompanyDirectoryPage from './pages/CompanyDirectoryPage';
import ResumePage from './pages/ResumePage';
import ResumeAdminPage from './pages/ResumeAdminPage';
import TimeCampReportPage from './pages/TimeCampReportPage';
import RndCreditsPage from './pages/RndCreditsPage';
import VendorCustomerAdminPage from './pages/VendorCustomerAdminPage';
import MileageRatesPage from './pages/MileageRatesPage';
import ConfigurationPage from './pages/ConfigurationPage';
import TimeOffConfigurationPage from './pages/TimeOffConfigurationPage';
import NotificationsConfigurationPage from './pages/NotificationsConfigurationPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingAdminPage from './pages/OnboardingAdminPage';
import SurveysPage from './pages/SurveysPage';
import SurveyEditorPage from './pages/SurveyEditorPage';
import TakeSurveyPage from './pages/TakeSurveyPage';
import SurveyResponsesPage from './pages/SurveyResponsesPage';
import PayrollPage from './pages/PayrollPage';
// Hours Breakdown: one-off admin report, self-contained. See the page header.
import HoursBreakdownPage from './pages/HoursBreakdownPage';
// EmployeeConnect timesheet: logging, approvals, reports and admin.
import TimesheetPage from './pages/TimesheetPage';
import TimesheetApprovalsPage from './pages/TimesheetApprovalsPage';
import TimesheetReportsPage from './pages/TimesheetReportsPage';
import TimesheetAdminPage from './pages/TimesheetAdminPage';
import { PAYROLL_ENABLED, PAYROLL_PERMISSION } from './config/payrollFeature';

/* Components */
import Sidebar from './components/navigation/Sidebar';
import ImpersonationBar from './components/shared/ImpersonationBar';
import BottomNav from './components/navigation/BottomNav';
import RoleBasedRoute from './components/shared/RoleBasedRoute';
import ToastHost from './components/ToastHost';
import TutorialHost from './tutorials/TutorialHost';

import './App.css';

function EmployeeEditRedirect() {
    const { id } = useParams();
    if (!id) return <Navigate to="/admin/employees" replace />;
    return <Navigate to={`/admin/employees/${id}/edit`} replace />;
}

// The timesheet validator now lives inside View Hours; keep old links working,
// filters and all.
function TimesheetValidatorRedirect() {
    const { search } = useLocation();
    return <Navigate to={`/view-hours${search}`} replace />;
}

// Timesheet admin moved under Admin > Configuration; old links keep their tab.
function TimesheetAdminRedirect() {
    const { search } = useLocation();
    return <Navigate to={`/admin/timesheet${search}`} replace />;
}

function App() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

    return (
        <div className="App">
            <ToastHost />
            <Router>
                <AuthGate appName="EmployeeConnect">
                    <AuthProvider>
                        <TutorialHost>
                        <div
                            className="app-shell"
                            data-sidebar-collapsed={sidebarCollapsed ? 'true' : 'false'}
                        >
                            <Sidebar
                                collapsed={sidebarCollapsed}
                                onToggle={() => setSidebarCollapsed((v) => !v)}
                            />

                            <div className="main-content">
                                <ImpersonationBar />
                                <div className="p-3">
                                    <Routes>
                                        <Route path="/" element={<HomePage />} />

                                        <Route path="/me" element={<MePage />} />

                                        {/* The journey page gates itself: it is only
                                            reachable by somebody who has been put through
                                            onboarding and has not finished it. */}
                                        <Route path="/onboarding" element={<OnboardingPage />} />
                                        <Route path="/me/resume" element={<ResumePage />} />
                                        <Route path="/directory" element={<CompanyDirectoryPage />} />

                                        <Route
                                            path="/admin"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_roles', 'manage_hiring', 'manage_resumes', 'manage_rd_credits', 'manage_expense_vendors', 'manage_mileage_rates', 'manage_onboarding', 'manage_payroll', 'create_surveys']}>
                                                    <AdminPage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/employees"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_roles']}>
                                                    <EmployeesPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/employees/:id"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_roles']}>
                                                    <EmployeeEditRedirect />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/employees/:id/edit"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_roles']}>
                                                    <MePage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/employees/:id/resume"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_resumes']}>
                                                    <ResumePage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/resumes"
                                            element={
                                                <RoleBasedRoute permission="manage_resumes">
                                                    <ResumeAdminPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route path="/time-off" element={<TimeOffPage />} />
                                        <Route path="/time-off/create" element={<TimeOffCreatePage />} />
                                        <Route path="/time-off/requests" element={<TimeOffManagePage />} />
                                        <Route path="/time-off/calendar" element={<TimeOffCalendarViewPage />} />
                                        <Route path="/time-off/planner" element={<VacationPlansPage />} />
                                        <Route path="/time-off/planner/:planId" element={<TimeOffPlannerPage />} />
                                        <Route path="/time-off/:id" element={<TimeOffRequestPage />} />
                                        <Route path="/view-hours" element={<ViewHoursPage />} />
                                        {/* The EmployeeConnect timesheet is admin-only while it is in
                                            preview, here and on the API (server/index.js). */}
                                        <Route
                                            path="/timesheet"
                                            element={
                                                <RoleBasedRoute adminOnly>
                                                    <TimesheetPage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route
                                            path="/timesheet/approvals"
                                            element={
                                                <RoleBasedRoute adminOnly>
                                                    <TimesheetApprovalsPage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route
                                            path="/timesheet/reports"
                                            element={
                                                <RoleBasedRoute adminOnly>
                                                    <TimesheetReportsPage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route path="/timesheet/admin" element={<TimesheetAdminRedirect />} />
                                        <Route path="/expenses" element={<ExpensePage />} />

                                        {/* The inbox is what Approvals means now; the tables it
                                            replaced stay reachable at /approvals/tables, which is
                                            where the inbox's own "Tables" button points. */}
                                        <Route
                                            path="/approvals"
                                            element={<ApprovalsInboxPage />}
                                        />

                                        <Route
                                            path="/approvals/tables"
                                            element={<ApprovalsPage />}
                                        />

                                        <Route
                                            path="/approvals/payroll-view"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_payroll', 'approve_expenses', '*']}>
                                                    <ExpensePayrollViewPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/approvals-grid"
                                            element={<ApprovalsGridPage />}
                                        />

                                        {/* Where the inbox lived while it was being built, kept
                                            so anyone who bookmarked it still lands on it. */}
                                        <Route
                                            path="/approvals-new"
                                            element={<Navigate to="/approvals" replace />}
                                        />

                                        <Route path="/admin/roles" element={<Navigate to="/admin/employees" replace />} />

                                        <Route path="/admin/invites" element={<Navigate to="/admin/employees" replace />} />

                                        {/* Close dates, Hours Breakdown, vendors, mileage rates, time
                                            off and the timesheet. Each page keeps its own guard; this
                                            one admits anybody who can reach at least one of them
                                            (admins hold every permission). */}
                                        <Route
                                            path="/admin/configuration"
                                            element={
                                                <RoleBasedRoute
                                                    anyOf={[
                                                        'manage_all_employees',
                                                        'manage_expense_vendors',
                                                        'manage_mileage_rates',
                                                        'manage_roles',
                                                        'create_surveys',
                                                        ...(PAYROLL_ENABLED ? [PAYROLL_PERMISSION] : []),
                                                    ]}
                                                >
                                                    <ConfigurationPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        {/* Admin only while the timesheet is in preview, like the
                                            /timesheet pages. */}
                                        <Route
                                            path="/admin/timesheet"
                                            element={
                                                <RoleBasedRoute adminOnly>
                                                    <TimesheetAdminPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/close-dates"
                                            element={
                                                <RoleBasedRoute permission="manage_all_employees">
                                                    <CloseDatesPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/reports/timecamp"
                                            element={
                                                <RoleBasedRoute permission="view_timecamp_breakdown">
                                                    <TimeCampReportPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/reports/timesheet-validator"
                                            element={<TimesheetValidatorRedirect />}
                                        />

                                        <Route
                                            path="/admin/rd-credits"
                                            element={
                                                <RoleBasedRoute permission="manage_rd_credits">
                                                    <RndCreditsPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        {/* Payroll is a company-specific module behind its own
                                            flag; turning it off leaves no route to hit. */}
                                        {PAYROLL_ENABLED ? (
                                            <Route
                                                path="/admin/payroll"
                                                element={
                                                    <RoleBasedRoute permission={PAYROLL_PERMISSION}>
                                                        <PayrollPage />
                                                    </RoleBasedRoute>
                                                }
                                            />
                                        ) : null}

                                        {PAYROLL_ENABLED ? <Route
                                            path="/admin/time-off"
                                            element={
                                                <RoleBasedRoute permission={PAYROLL_PERMISSION}>
                                                    <TimeOffConfigurationPage />
                                                </RoleBasedRoute>
                                            }
                                        /> : null}

                                        <Route
                                            path="/admin/notifications"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_roles', 'create_surveys']}>
                                                    <NotificationsConfigurationPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route path="/surveys" element={<SurveysPage />} />
                                        <Route path="/surveys/:surveyId" element={<TakeSurveyPage />} />
                                        <Route
                                            path="/surveys/:surveyId/responses"
                                            element={
                                                <RoleBasedRoute permission="create_surveys">
                                                    <SurveyResponsesPage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route
                                            path="/surveys/:surveyId/edit"
                                            element={
                                                <RoleBasedRoute permission="create_surveys">
                                                    <SurveyEditorPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/onboarding"
                                            element={
                                                <RoleBasedRoute permission="manage_onboarding">
                                                    <OnboardingAdminPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        {/* Hours Breakdown (one-off admin report) */}
                                        <Route
                                            path="/admin/hours-breakdown"
                                            element={
                                                <RoleBasedRoute adminOnly>
                                                    <HoursBreakdownPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/vendors-customers"
                                            element={
                                                <RoleBasedRoute permission="manage_expense_vendors">
                                                    <VendorCustomerAdminPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/mileage-rates"
                                            element={
                                                <RoleBasedRoute permission="manage_mileage_rates">
                                                    <MileageRatesPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route
                                            path="/admin/hiring"
                                            element={
                                                <RoleBasedRoute permission="manage_hiring">
                                                    <HiringDashboardPage />
                                                </RoleBasedRoute>
                                            }
                                        />

                                        <Route path="/employees" element={<Navigate to="/admin/employees" replace />} />
                                        <Route
                                            path="/employees/:id"
                                            element={
                                                <RoleBasedRoute anyOf={['manage_all_employees', 'manage_coached_employees', 'manage_roles']}>
                                                    <EmployeeEditRedirect />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route
                                            path="/employees/:id/edit"
                                            element={
                                                <RoleBasedRoute permission="manage_all_employees">
                                                    <MePage />
                                                </RoleBasedRoute>
                                            }
                                        />
                                        <Route path="/roles" element={<Navigate to="/admin/employees" replace />} />

                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </div>
                            </div>
                        </div>

                        <BottomNav />
                        </TutorialHost>
                    </AuthProvider>
                </AuthGate>
            </Router>
        </div>
    );
}

export default App;